/**
 * Inline theme references in chat text: `[theme id="{uuid}" colors="#AABBCC,#DDEEFF"]`.
 *
 * The selector's Edit action used to prefill the positional label ("Custom theme 1") with the id
 * hidden alongside it. Positional labels are meaningless once themes are added or deleted, so a
 * theme is referenced the way blocks are — a token in the message text that the composer and the
 * chat bubble both render as a chip showing the theme's first two colors, and that the agent reads
 * as the theme's id.
 */

const THEME_REF_PATTERN = /\[theme id="([0-9a-fA-F-]{36})" colors="([^"]*)"\]/g;

export interface ThemeRef {
  themeId: string;
  /** The theme's brand colors, in order; the chip shows the first two. */
  colors: string[];
}

export function serializeThemeRef({ themeId, colors }: ThemeRef): string {
  // Hex codes only, so no escaping is needed — anything else is dropped rather than smuggled
  // into a token the parser would then fail to match.
  const safeColors = colors.filter(color => /^#[0-9a-fA-F]{3,8}$/.test(color));
  return `[theme id="${themeId}" colors="${safeColors.join(",")}"]`;
}

export interface ThemeRefSegment {
  text?: string;
  themeRef?: ThemeRef;
}

/** Split text into plain segments and theme references, in order. */
export function splitThemeRefs(text: string): ThemeRefSegment[] {
  const segments: ThemeRefSegment[] = [];
  // A fresh regex per call: the shared one is global, so lastIndex would carry across calls.
  const pattern = new RegExp(THEME_REF_PATTERN.source, "g");
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index) });
    }
    segments.push({
      themeRef: {
        themeId: match[1],
        colors: match[2] ? match[2].split(",") : [],
      },
    });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex) });
  }
  return segments;
}

export function hasThemeRef(text: string): boolean {
  return new RegExp(THEME_REF_PATTERN.source).test(text);
}
